import React, { FC, FormEvent, ReactElement, useEffect, useState } from 'react';
import Modal from 'react-modal';
import './LoginModal.sass';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  getContentDataIntro,
  getEventKey,
  getUserEmail,
  // getUserRegistrationFields,
  getIsRegisterInProgress,
  setLoggedIn,
  setAdminLoggedIn,
  setUserEmail,
  setUserRegistrationFields,
  setIsRegisterInProgress,
} from '../../../stores/reducers/userData';
import { internalizationState } from '../../../stores/reducers/sections';
import routes from '../../../config/routes';
import Icon from '../../Icon';
import {
  authByEmail,
  checkAuthorization,
  loginAdmin,
  // registerToEvent,
  restorePassword
} from '../../../api';
import localeFieldResolver from '../../../helpers/locale-field-finder';
import responseErrorChecker from '../../../helpers/response-error-checker';
import { getAllStyle } from '../../../stores/reducers/sections';
import moment from 'moment';
import {storageFactory} from '../../../helpers/storage';

const local = storageFactory(() => localStorage);

export const PASSWORD_RESTORE_TIME = 'password_restore_time';

Modal.setAppElement('#root');

interface Props {
  isOpen: boolean;
  closeModal?: () => void;
  title?: string;
  notice?: string;
  isAdminAuth?: boolean;
  goal?: string;
  isConfirmRegistrationMode?: boolean;
  isFormHidden?: boolean;
}

let timer: any = null;

const LoginModal: FC<Props> = ({
  isOpen,
  closeModal,
  title,
  notice,
  isAdminAuth,
  goal,
  isConfirmRegistrationMode,
  isFormHidden = false,
}): ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const emailFromStore = useSelector(getUserEmail);
  const [password, setPassword] = useState('');
  const eventKey = useSelector(getEventKey);
  // const userRegistrationFields = useSelector(getUserRegistrationFields);
  const [isRestoringPassword, setIsRestoringPassword] = useState(!isConfirmRegistrationMode);
  const { eventName, eventName_en } = useSelector(getContentDataIntro);
  const localizedEventNames = { eventName, eventName_en };
  const [selfNotice, setSelfNotice] = useState('');
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const isRegisterInProgress = useSelector(getIsRegisterInProgress);
  const { activeLangState } = useSelector(internalizationState);
  const [isTimerVisible, setTimerVisible] = useState<boolean>(false);
  const [passwordRestoreTime, setPasswordRestoreTime] = useState<string | null>(null);

  const localizeData = {
    password_ru: 'Одноразовый код',
    password_en: 'Оne-time code',
    get_password_ru: 'Далее',
    get_password_en: 'Next',
    password_confirm_ru: 'Введите одноразовый код из почты и нажмите "Войти"',
    password_confirm_en: 'Enter the one-time code from your email and click "Login"',
    login_ru: 'Войти',
    login_en: 'Login',
    login_event_ru: 'Вход на мероприятие',
    login_event_en: 'Entry to the event',
    enter_email_ru: 'Введите почту, которую вы указали при регистрации',
    enter_email_en: 'Enter the email you provided during registration',
    wrong_pass_ru: 'Вы ввели одноразовый код',
    wrong_pass_en: 'Incorrect one-time code',
    error_occured_ru: 'Возникла ошибка, повторите попытку позже',
    error_occured_en: 'An error occured, please try again later',
    success_message_ru: `Одноразовый код для входа на мероприятие "${localeFieldResolver(localizedEventNames, 'eventName', activeLangState) as string}" отправлен вам на почту ${email || emailFromStore}`,
    success_message_en: `The one-time code to enter the event ${localeFieldResolver(localizedEventNames, 'eventName', activeLangState) as string} has been sent to your email ${email || emailFromStore}`,
    failed_message_ru: 'Email не зарегистрирован на мероприятие. Пройдите регистрацию.',
    failed_message_en: 'Email is not registered for the event. Please register.',
    register_now_ru: 'Зарегистрироваться',
    register_now_en: 'Register now',
    change_email_ru: 'Изменить email',
    change_email_en: 'Change email address',
    restore_password_ru: 'Восстановить пароль',
    restore_password_en: 'Resotore password',
    close_ru: 'Закрыть',
    close_en: 'Close',
  };

  const localizedName = (field: string): string => localeFieldResolver(localizeData, field, activeLangState);
  const styles = useSelector(getAllStyle);

  const startTimer = (unix: number | null): void => {
    if (!unix) {
      setPasswordRestoreTime(null);
      return;
    }

    setTimeout(() => {
      setTimerVisible(true);
    }, 1000);

    local.setItem(PASSWORD_RESTORE_TIME, unix.toString());

    const interval = 1000;

    timer = setInterval(function(){
      const now = moment().unix();
      const diffTime = unix - now;
      const diff = moment.duration(diffTime * 1000, 'milliseconds') as any as number;


      if (diffTime <= 0) {
        clearTimer();
        return;
      }

      const duration = moment.duration(diff - interval, 'milliseconds');
      setPasswordRestoreTime(`${duration.minutes()}:${duration.seconds() >= 10 ? duration.seconds() : '0' + duration.seconds()}`);
    }, interval);
  }

  const clearTimer = (): void => {
    clearInterval(timer);
    setPasswordRestoreTime(null);
    local.removeItem(PASSWORD_RESTORE_TIME);
  }

  useEffect(() => {
    if (isOpen && local.getItem(PASSWORD_RESTORE_TIME)) {
      if (timer) clearInterval(timer);
      startTimer(Number(local.getItem(PASSWORD_RESTORE_TIME)));
    }

    if (!isOpen) setTimerVisible(false);

    if (isAdminAuth) {
      setIsRestoringPassword(false);
    }

  }, [isOpen, isAdminAuth]);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitDisabled(true);

    try {
      window.dispatchEvent((window as any).authFinished);

      if (isAdminAuth) {
        const loginAdminResponse = await loginAdmin(email, password) as Response;
        if (loginAdminResponse.status === 200) {
          dispatch(setAdminLoggedIn(true));
          if (closeModal) closeModal();
        }
      } else {
        const authByEmailResponse = await authByEmail(email || emailFromStore, password, eventKey) as Response;
        const authByEmailPermission = await responseErrorChecker(authByEmailResponse, activeLangState);

        if (!authByEmailPermission) return;

        if (authByEmailResponse.status === 200) {
          if (!isRegisterInProgress) {
            const checkAuthorizationResponse = await checkAuthorization(eventKey) as Response;
            if (checkAuthorizationResponse.status === 403) history.push(routes.REGISTER);
          }

          // if (userRegistrationFields && Object.keys(userRegistrationFields).length) {
          //   const registerToEventResponse = await registerToEvent(userRegistrationFields, eventKey) as Response;
          //   if (registerToEventResponse.status === 200) {
          //     dispatch(setUserRegistrationFields([]));
          //   } else {
          //     console.error('registerToEvent: bad response!');
          //   }
          // }

          dispatch(setLoggedIn(true));
          dispatch(setUserEmail(email));
          dispatch(setIsRegisterInProgress(false));
          if (closeModal) closeModal();
          history.push(routes.LIVE);
        }
      }
    } catch (error) {
      console.error(`Login error: ${error}`);
    } finally {
      setIsSubmitDisabled(false);
    }
  };

  const handleRestorePassword = (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitDisabled(true);

    window.dispatchEvent((window as any).clickRestore);

    restorePassword(email || emailFromStore, eventKey).then((response) => {
      if (response) {
        const { status, code, passwordUpdateLock = null, error } = response;
        if (status) {
          setIsRestoringPassword(false);
          setIsSubmitDisabled(false);
          setSelfNotice(localizedName('success_message_ru'));
          if (passwordUpdateLock) {
            if (timer) clearInterval(timer);
            startTimer(passwordUpdateLock);
          }
        } else if (code === 404 || error?.message) {
          setTimeout(() => {
            setIsSubmitDisabled(false);
          }, 1000);

          let errorMessage = error?.message ?? null;

          if (errorMessage && (errorMessage as string).toLowerCase().includes('пароль')) {
            errorMessage = errorMessage.replace(/(П|п)ароль/g, 'Код') + ` ${email || emailFromStore}`;
            setIsRestoringPassword(false);
          }

          setSelfNotice(errorMessage ?? localizedName('failed_message_ru'));
        }
      }
    });
  };

  const goToRegistration = () => {
    if (closeModal) closeModal();
    history.push(routes.REGISTER);
  };

  const onModalClose = () => {
    setIsRestoringPassword(true);
    setSelfNotice('');
    if (closeModal) closeModal();
  }

  return (
    <Modal
      className="modal-login-content"
      overlayClassName="modal-login-overlay"
      isOpen={isOpen}
      onRequestClose={onModalClose}>
      {isRestoringPassword ? (
        <>
          <h3 className="modal-title">{localizedName('login_event_ru')}</h3>
          {closeModal && (
            <button className="modal-close" type="button" onClick={() => onModalClose()}>
              <Icon name="close" />
            </button>
          )}
          {(notice || selfNotice) && <p className="modal-notice">{notice || selfNotice}</p>}
          <form className="modal-form" onSubmit={(e) => handleRestorePassword(e)}>
            <input
              id="modalLogin"
              type="email"
              placeholder={localizedName('enter_email_ru')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {/* {
              !!passwordRestoreTime === true &&
              <>
                <p>
                  Получить одноразовый код повторно {passwordRestoreTime}
                </p>
                <br />
              </>
            } */}
            <div className="login-form-buttons">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!email || isSubmitDisabled}
                style={{
                  backgroundColor: !email || isSubmitDisabled? '' : styles.loginPopup.buttonColor || 'var(--colorPrimary)',
                  color:  !email || isSubmitDisabled? '' : styles.loginPopup.buttonFontColor || 'var(--colorWhite)',
                  pointerEvents: isSubmitDisabled ? 'none' : 'all',
                }}
              >
                {localizedName('get_password_ru')}
              </button>
              {!isAdminAuth && (
                <button
                  type="button"
                  className="btn--text"
                  onClick={goToRegistration}
                >
                  {localizedName('register_now_ru')}
                </button>
              )}
            </div>
          </form>
        </>
      ) : (
        <>
          <h3 className="modal-title">{title || localizedName('login_ru')}</h3>
          {closeModal && (
            <button className="modal-close" type="button" onClick={closeModal}>
              <Icon name="close" />
            </button>
          )}
          {(notice || selfNotice) && <p className="modal-notice" style={{color: '#fe3737'}}>{notice || selfNotice}</p>}
          {
            !isFormHidden
            ? <form className="modal-form" onSubmit={(e) => handleLogin(e)}>
                {(isAdminAuth) && (
                  <>
                    <label htmlFor="modalLogin">{isAdminAuth ? 'Login' : 'Email'}</label>
                    <input
                      id="modalLogin"
                      type={isAdminAuth ? 'text' : 'email'}
                      placeholder={isAdminAuth ? 'Login' : 'Email'}
                      value={email || emailFromStore}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </>
                )}

                <label htmlFor="modalPass">{localizedName('password_ru')}</label>
                <input
                  id="modalPass"
                  type="password"
                  placeholder={localizedName('password_confirm_ru')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {
                  (!isAdminAuth && isTimerVisible) && 
                  <div>
                    {
                      !!passwordRestoreTime === true
                      ? <>
                        <p style={{color: '#8b8b8b'}}>
                          Получить одноразовый код повторно: {passwordRestoreTime}
                        </p>
                        <br />
                      </>  
                      : <><p><a href='' style={{color: '#1b46a7'}} onClick={(e) => handleRestorePassword(e)}>Получить одноразовый код повторно</a></p><br /></>
                    
                    }
                  </div>
                }
                <div className="login-form-buttons">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!(email || emailFromStore) || !password || isSubmitDisabled}
                    style={{
                      backgroundColor: !(email || emailFromStore) || !password ? '' : styles.loginPopup.buttonColor || 'var(--colorPrimary)',
                      color:  !(email || emailFromStore) || !password ? '' : styles.loginPopup.buttonFontColor || 'var(--colorWhite)',
                    }}
                  >
                    {(() => {
                      switch (activeLangState) {
                        case "ru": return isConfirmRegistrationMode ? 'Подтвердить' : 'Войти';
                        case "en": return isConfirmRegistrationMode ? 'Confirm' : 'Login';
                      }
                    })()}
                  </button>
                  <button type="button" className="btn" onClick={onModalClose}>
                    {localizedName('close_ru')}
                  </button>
                  {/* {!(isAdminAuth || isConfirmRegistrationMode) && (
                    <>
                      <button type="button" className="btn--text" onClick={goToRegistration}>
                        Зарегистрироваться
                      </button>
                      <button type="button" className="btn--text" onClick={() => setIsRestoringPassword(true)}>
                        {localizedName('change_email_ru')}
                      </button>
                    </>
                  )} */}
                </div>
              </form>
            : <div className="login-form-buttons">
                <button type="button" className="btn" onClick={onModalClose}>
                  {localizedName('close_ru')}
                </button>
              </div>
          }
        </>
      )}
    </Modal>
  );
};

export default LoginModal;
